import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

import { useStepperContext } from '@/components/shared/Stepper/StepperContext.tsx';
import { allTravelDnaSteps } from '@/_constants/TravelDnaStep.ts';
import TravelDnaStep from '@/_constants/TravelDnaStep.ts';
import { dnaSchema } from '$/requests/UpsertDnaRequest.ts';
import type { TravelDnaFormData } from '$/models/Dna.d.ts';
import type { OnCompleteArgs } from '@/components/functional/TravelDnaManipulationForm/TravelDnaManipulationForm.tsx';
import DnaStepLayout from '@/components/functional/TravelDnaManipulationForm/shared/DnaStepLayout.tsx';
import DnaSlider from '@/components/functional/TravelDnaManipulationForm/shared/DnaSlider.tsx';
import DnaPrimaryButton from '@/components/functional/TravelDnaManipulationForm/shared/DnaPrimaryButton.tsx';
import DnaError from '@/components/functional/TravelDnaManipulationForm/shared/DnaError.tsx';

const yourPlansSchema = dnaSchema.pick({
  how_much_do_you_like_a_plan: true,
});

export type YourPlansSchema = z.infer<typeof yourPlansSchema>;

type YourPlansProps = {
  onComplete?: (args: OnCompleteArgs) => Promise<void>;
  onCompleteWhenDirty?: (args: OnCompleteArgs) => Promise<void>;
};

function YourPlans({ onComplete, onCompleteWhenDirty }: YourPlansProps) {
  const { t } = useTranslation();
  const { nextStep, setContextData, contextData } = useStepperContext<
    typeof allTravelDnaSteps,
    TravelDnaFormData
  >();
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const {
    control,
    handleSubmit,
    watch,
    formState: { isDirty },
  } = useForm<YourPlansSchema>({
    resolver: zodResolver(yourPlansSchema),
    defaultValues: {
      how_much_do_you_like_a_plan:
        contextData?.how_much_do_you_like_a_plan ?? 3,
    },
  });

  const value = watch('how_much_do_you_like_a_plan') ?? 3;

  const onSubmit = (data: YourPlansSchema) => {
    void (async () => {
      try {
        setIsPending(true);
        setError(null);
        const context = { ...contextData, ...data };
        const args: OnCompleteArgs = {
          step: TravelDnaStep.YOUR_PLANS,
          data,
          context,
        };
        const shouldPersist =
          isDirty || contextData?.how_much_do_you_like_a_plan == null;

        if (shouldPersist) {
          setContextData((previous) => ({ ...previous, ...data }));
          await onCompleteWhenDirty?.(args);
        }

        await onComplete?.(args);
        nextStep();
      } catch (caughtError) {
        setError(
          caughtError instanceof Error
            ? caughtError
            : new Error(String(caughtError))
        );
      } finally {
        setIsPending(false);
      }
    })();
  };

  return (
    <DnaStepLayout
      eyebrow={t('travelDnaPage.yourPlans.header')}
      title={t('travelDnaPage.yourPlans.subheader')}
      maxWidthClassName="max-w-[770px]"
    >
      <form
        className="mt-24 flex w-full flex-col items-center"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <DnaError error={error} className="mb-7" />
        <Controller
          control={control}
          name="how_much_do_you_like_a_plan"
          render={({ field }) => (
            <DnaSlider
              value={field.value ?? 3}
              onChange={field.onChange}
              leftLabel={t('travelDnaPage.yourPlans.leftLabel')}
              leftDescription={t('travelDnaPage.yourPlans.leftDescription')}
              rightLabel={t('travelDnaPage.yourPlans.rightLabel')}
              rightDescription={t('travelDnaPage.yourPlans.rightDescription')}
              caption={t(`travelDnaPage.yourPlans.captions.${value}`)}
              disabled={isPending}
              max={4}
            />
          )}
        />

        <DnaPrimaryButton className="mt-28" loading={isPending} type="submit">
          {t('travelDnaPage.yourPlans.continue')}
        </DnaPrimaryButton>
      </form>
    </DnaStepLayout>
  );
}

export default YourPlans;
